/**
 * Ensure that the correct module is set in the store based on the route.
 *
 * Plan-restricted pages:
 * - In-app navigation (clicking a gated nav item): abort so the user stays on the
 *   current page; the sidebar components open the upsell modal on click.
 * - Direct navigation (deep-link / refresh): let it through so the page's header stays
 *   visible and the upsell is shown inline in place of the content (Page component via
 *   useRouteUpsellGuard) — aborting a direct load would leave a blank screen.
 */
export default defineNuxtRouteMiddleware((to, from) => {
    const { currentModule, modules, setCurrentModule, unsetCurrentModule } = useModules();
    const { findRestrictedPermissionKey } = useRouteUpsellGuard();

    const prefix = to.path.split('/')[1];
    if (prefix) {
        const module =
            modules.value.find((m) => m.basePath && to.path.startsWith(m.basePath)) ??
            modules.value.find((m) => m.key === prefix) ??
            (prefix === 'bankaccount' ? modules.value.find((m) => m.key === 'invoicing') : undefined);

        if (module) {
            const isDirectNavigation = !from.name;
            // For in-app navigation to a gated page, stay put (the sidebar opens the modal).
            // Direct loads fall through to render the inline upsell.
            if (!isDirectNavigation && findRestrictedPermissionKey(to)) {
                return abortNavigation();
            }

            if (currentModule.value?.key !== module.key) {
                setCurrentModule(module.key, false);
            }
            updateMetaTitle(module.key);
            return;
        }
    }

    unsetCurrentModule();
});

function updateMetaTitle(moduleKey: string) {
    const { t } = useTranslation();
    if (t('modules.' + moduleKey) !== 'modules.' + moduleKey) {
        useSeoMeta({
            title: t(`modules.${moduleKey}`) + ' - TrustUp Pro Manager',
        });
    }
}
